import type { MutableRefObject } from 'react';
import React, { useCallback, useMemo } from 'react';
import type { TooltipProps } from 'recharts';
import { useTheme } from '@mui/styles';
import type { ChartReportPart, ReportDateTimeframe, ReportProps } from 'c-reports/Types';
import { ReportType } from 'c-reports/Types';
import type { TranslationPath } from 'c-translation';
import { useCommonTranslation } from 'c-translation';
import CustomTooltip from 'c-main/Components/Campaign/CampaignReports/Views/ByField/CustomTooltip';
import { FieldReportType } from 'c-main/Components/Campaign/CampaignReports/types';
import type { PCAReportCountField, PCAReportField } from 'c-sdk';
import { EsPCAReportField, PCAReportMetric } from 'c-sdk';
import {
    GroupByFieldWidgetReport,
    LeaderboardFetchingWidgetReport,
    LeaderboardWidgetReport,
    MapWidgetReport,
    OverlayBarsFetchingWidgetReport,
    OverlayBarsWidgetReport,
    StaticChartWidgetReport,
} from './summaryWidgetReports';
import type { SummaryWidgetConfig, SummaryWidgetMetric } from './summaryCustomizationConfig';
import {
    SUMMARY_WIDGET_GROUP_BY_PCA_FIELD_MAP,
    SUMMARY_WIDGET_GROUP_BY_TRANSLATION_KEYS,
} from './summaryCustomizationConfig';
import { buildStandardSummaryExportData } from './campaignAnalyticsUtils';

type SummaryWidgetDefinition = {
    id: string;
    report: ReportProps<any, any>;
    expandedReport?: ReportProps<any, any>;
};

type VisibleSummaryWidgetDefinition = SummaryWidgetDefinition & {
    title: string;
    expandedTitle?: string;
    isHeatmap: boolean;
    isPrimary: boolean;
    kind: SummaryWidgetConfig['kind'];
    displayMode: SummaryWidgetConfig['displayMode'];
};

type MetricChartLabels = {
    actualLabel: string;
    actualLabelOverride?: string;
    expectedLabel: string;
    percentageLabel: string;
    missingLabel: string;
};

type Params = {
    id: number;
    source: string;
    environmentMode: string | null | undefined;
    fieldFilters: Record<string, string[]>;
    isDsp: boolean;
    isAverageAudienceMode: boolean;
    isGeopathOnly: boolean;
    selectedAudienceLabelForCharts: string | null;
    showDefaultDspExpectedLine: (widget: SummaryWidgetConfig) => boolean;
    showDefaultDspExpectedBar: (widget: SummaryWidgetConfig) => boolean;
    showDefaultDspTargetPercent: (widget: SummaryWidgetConfig) => boolean;
    actualDeliveredLabelImpacts: string;
    shareOfTotalLabel: string;
    percentLabelDecimals: number;
    actualLabelImpacts: string;
    actualLabelPlays: string;
    expectedLabelForImpactsCharts: string;
    expectedLabelImpacts: string;
    expectedLabelPlays: string;
    percentageLabelImpacts: string;
    percentageLabelPlays: string;
    performanceLabel: string;
    inScheduleLabel: string;
    outScheduleLabel: string;
    unbookedLabel: string;
    timeframe: ReportDateTimeframe | null;
    countFields: PCAReportCountField[];
    metricTitles: Record<SummaryWidgetMetric, string>;
    summaryTitleBy: string;
    summaryTitleHeatmap: string;
    summaryTitleJoin: string;
    summaryTitleRunningTotal: string;
    dateLabelLabel: string;
    visibleWidgetConfigs: SummaryWidgetConfig[];
    initialStartDate: Date;
    initialEndDate: Date;
    summaryLoadingState: any;
    latestSummaryWindow: MutableRefObject<{
        start: Date;
        end: Date;
        timeframe: ReportDateTimeframe;
    } | null>;
    getWidgetData: (widget: SummaryWidgetConfig) => any;
    getMetricChartLabels: (metric: SummaryWidgetMetric) => MetricChartLabels;
    fetchExtraWidgetData: (
        widget: SummaryWidgetConfig,
        start: Date,
        end: Date,
        timeframe: ReportDateTimeframe,
    ) => Promise<any[]>;
    /** UL-8133 -- changes when a vistar_us2 target series loads, so fetched widgets redraw. */
    targetSeriesKey?: string;
    buildSummaryExportData: (
        rows: any[] | null | undefined,
        metric: SummaryWidgetMetric,
        xAxisLabels: string[],
    ) => any[] | undefined;
};

export const useCampaignAnalyticsSummaryWidgets = ({
    id,
    source,
    environmentMode,
    fieldFilters,
    isDsp,
    isAverageAudienceMode,
    isGeopathOnly,
    selectedAudienceLabelForCharts,
    showDefaultDspExpectedLine,
    showDefaultDspExpectedBar,
    showDefaultDspTargetPercent,
    actualDeliveredLabelImpacts,
    shareOfTotalLabel,
    percentLabelDecimals,
    actualLabelImpacts,
    actualLabelPlays,
    expectedLabelForImpactsCharts,
    expectedLabelImpacts,
    expectedLabelPlays,
    percentageLabelImpacts,
    percentageLabelPlays,
    performanceLabel,
    inScheduleLabel,
    outScheduleLabel,
    unbookedLabel,
    timeframe,
    countFields,
    metricTitles,
    summaryTitleBy,
    summaryTitleHeatmap,
    summaryTitleJoin,
    summaryTitleRunningTotal,
    dateLabelLabel,
    visibleWidgetConfigs,
    initialStartDate,
    initialEndDate,
    summaryLoadingState,
    latestSummaryWindow,
    getWidgetData,
    getMetricChartLabels,
    fetchExtraWidgetData,
    buildSummaryExportData,
    targetSeriesKey = 'none',
}: Params) => {
    const theme = useTheme();
    const t = useCommonTranslation();

    const generateParts = useCallback(
        ({
            actual,
            expected,
            actualLabelOverride,
        }: {
            actual: string;
            expected?: string;
            actualLabelOverride?: string;
        }) => {
            if (isDsp) {
                // Geopath, audited and averages modes all treat the comparison audience as
                // the primary series, with total DSP impressions as the grey baseline.
                if (
                    (isGeopathOnly ||
                        isAverageAudienceMode ||
                        selectedAudienceLabelForCharts != null) &&
                    expected
                ) {
                    return [
                        {
                            dataKey: expected,
                            filterRole: 'delivered',
                            comparisonSeries: false,
                            lineProps: {
                                stroke: theme.palette.primary.main,
                            },
                            barProps: { fill: theme.palette.primary.main },
                        },
                        {
                            dataKey: actual,
                            labelOverride: actualLabelOverride,
                            filterRole: 'expected',
                            comparisonSeries: true,
                            lineProps: {
                                stroke: theme.palette.grey['500'],
                                strokeDasharray: '5 5',
                            },
                            barProps: { fill: theme.palette.grey['500'] },
                        },
                    ] as ChartReportPart[];
                }

                return [
                    {
                        dataKey: actual,
                        labelOverride: actualLabelOverride,
                        filterRole: 'delivered',
                        lineProps: { stroke: theme.palette.primary.main },
                        barProps: { fill: theme.palette.primary.main },
                    },
                ] as ChartReportPart[];
            }

            return [
                {
                    dataKey: expected,
                    filterRole: 'expected',
                    lineProps: {
                        stroke: theme.palette.grey['500'],
                        strokeDasharray: '5 5',
                    },
                    barProps: { fill: theme.palette.grey['500'] },
                },
                {
                    dataKey: actual,
                    filterRole: 'delivered',
                    lineProps: { stroke: theme.palette.primary.main },
                    barProps: { fill: theme.palette.primary.main },
                },
            ] as ChartReportPart[];
        },
        [
            isAverageAudienceMode,
            isDsp,
            isGeopathOnly,
            selectedAudienceLabelForCharts,
            theme.palette.grey,
            theme.palette.primary.main,
        ],
    );

    const dailyImpactParts = useMemo<ChartReportPart[]>(
        () =>
            generateParts({
                actual: actualLabelImpacts,
                expected: expectedLabelForImpactsCharts,
                actualLabelOverride: isDsp ? actualDeliveredLabelImpacts : undefined,
            }),
        [
            actualDeliveredLabelImpacts,
            actualLabelImpacts,
            expectedLabelForImpactsCharts,
            generateParts,
            isDsp,
        ],
    );

    const dailyPlayParts = useMemo<ChartReportPart[]>(
        () => generateParts({ actual: actualLabelPlays, expected: expectedLabelPlays }),
        [actualLabelPlays, expectedLabelPlays, generateParts],
    );

    const impactsTooltipLabel = useCallback(
        (
            reportType: FieldReportType,
            field: PCAReportField,
            expectedLabelOverride: string = expectedLabelForImpactsCharts,
        ) =>
            function ImpactsTooltipLabel(tooltipProps: TooltipProps<any, any>) {
                return (
                    <CustomTooltip
                        tooltipProps={tooltipProps}
                        expectedLabel={expectedLabelOverride}
                        percentageLabel={percentageLabelImpacts}
                        inScheduleLabel={inScheduleLabel}
                        outScheduleLabel={outScheduleLabel}
                        unbookedLabel={unbookedLabel}
                        timeFrame={timeframe}
                        type={reportType}
                        field={field}
                        metric={PCAReportMetric.Impacts}
                        countFields={isDsp ? [] : countFields}
                        hidePercentage={isDsp}
                        showPerformance={isDsp}
                        performanceLabel={performanceLabel}
                    />
                );
            },
        [
            countFields,
            expectedLabelForImpactsCharts,
            inScheduleLabel,
            isDsp,
            outScheduleLabel,
            percentageLabelImpacts,
            performanceLabel,
            timeframe,
            unbookedLabel,
        ],
    );

    const playsTooltipLabel = useCallback(
        (reportType: FieldReportType, field: PCAReportField) =>
            function PlaysTooltipLabel(tooltipProps: TooltipProps<any, any>) {
                return (
                    <CustomTooltip
                        tooltipProps={tooltipProps}
                        expectedLabel={expectedLabelPlays}
                        percentageLabel={percentageLabelPlays}
                        inScheduleLabel={inScheduleLabel}
                        outScheduleLabel={outScheduleLabel}
                        unbookedLabel={unbookedLabel}
                        timeFrame={timeframe}
                        type={reportType}
                        field={field}
                        metric={PCAReportMetric.Plays}
                        countFields={isDsp ? [] : countFields}
                        hidePercentage={isDsp}
                        showPerformance={isDsp}
                        performanceLabel={performanceLabel}
                    />
                );
            },
        [
            countFields,
            expectedLabelPlays,
            inScheduleLabel,
            isDsp,
            outScheduleLabel,
            percentageLabelPlays,
            performanceLabel,
            timeframe,
            unbookedLabel,
        ],
    );

    const getSummaryWidgetTitle = useCallback(
        (widget: SummaryWidgetConfig) => {
            if (widget.title) return widget.title;

            const metricTitle = metricTitles[widget.metric] ?? widget.metric;

            if (
                widget.kind === 'line' &&
                widget.groupBy.length === 1 &&
                widget.groupBy[0] === 'date'
            ) {
                return `${summaryTitleRunningTotal} ${metricTitle}`;
            }

            if (widget.kind === 'map') {
                return `${metricTitle} ${summaryTitleHeatmap}`;
            }

            const groupByLabels = widget.groupBy.map(groupBy =>
                t(
                    SUMMARY_WIDGET_GROUP_BY_TRANSLATION_KEYS[groupBy] ??
                        (`Modules.Main.Campaigns.Overview.Reports.fields.${groupBy}` as TranslationPath),
                ),
            );

            if (widget.displayMode === 'leaderboard') {
                return `Top ${groupByLabels.join(summaryTitleJoin)}`;
            }

            return `${metricTitle} ${summaryTitleBy} ${groupByLabels.join(summaryTitleJoin)}`;
        },
        [
            metricTitles,
            summaryTitleBy,
            summaryTitleHeatmap,
            summaryTitleJoin,
            summaryTitleRunningTotal,
            t,
        ],
    );

    const getWidgetReportField = useCallback((widget: SummaryWidgetConfig) => {
        if (widget.groupBy.length === 1) {
            return SUMMARY_WIDGET_GROUP_BY_PCA_FIELD_MAP[widget.groupBy[0]] ?? null;
        }

        if (
            widget.groupBy.length === 2 &&
            widget.groupBy[0] === 'display_owner' &&
            widget.groupBy[1] === 'display_format'
        ) {
            return EsPCAReportField.DisplayOwnerFormat as unknown as PCAReportField;
        }

        return null;
    }, []);

    const getWidgetParts = useCallback(
        (widget: SummaryWidgetConfig) => {
            if (widget.kind === 'line') {
                if (showDefaultDspExpectedLine(widget)) {
                    return [
                        {
                            dataKey: expectedLabelForImpactsCharts,
                            labelOverride: expectedLabelImpacts,
                            filterRole: 'expected',
                            comparisonSeries: true,
                            lineProps: {
                                stroke: theme.palette.grey['500'],
                                strokeDasharray: '5 5',
                            },
                            barProps: { fill: theme.palette.grey['500'] },
                        },
                        {
                            dataKey: actualLabelImpacts,
                            labelOverride: actualDeliveredLabelImpacts,
                            filterRole: 'delivered',
                            comparisonSeries: false,
                            lineProps: { stroke: theme.palette.primary.main },
                            barProps: { fill: theme.palette.primary.main },
                        },
                    ] as ChartReportPart[];
                }

                return widget.metric === 'plays' ? dailyPlayParts : dailyImpactParts;
            }

            if (widget.kind === 'bar') {
                if (showDefaultDspExpectedBar(widget)) {
                    return [
                        {
                            dataKey: expectedLabelForImpactsCharts,
                            labelOverride: expectedLabelImpacts,
                            filterRole: 'expected',
                            comparisonSeries: true,
                            lineProps: {
                                stroke: theme.palette.grey['500'],
                                strokeDasharray: '5 5',
                            },
                            barProps: { fill: theme.palette.grey['500'] },
                        },
                        {
                            dataKey: actualLabelImpacts,
                            labelOverride: actualDeliveredLabelImpacts,
                            filterRole: 'delivered',
                            comparisonSeries: false,
                            lineProps: { stroke: theme.palette.primary.main },
                            barProps: { fill: theme.palette.primary.main },
                        },
                    ] as ChartReportPart[];
                }

                const labels = getMetricChartLabels(widget.metric);
                return generateParts({
                    actual: labels.actualLabel,
                    actualLabelOverride: labels.actualLabelOverride,
                    expected: labels.expectedLabel,
                });
            }

            return [] as ChartReportPart[];
        },
        [
            actualDeliveredLabelImpacts,
            actualLabelImpacts,
            dailyImpactParts,
            dailyPlayParts,
            expectedLabelForImpactsCharts,
            expectedLabelImpacts,
            generateParts,
            getMetricChartLabels,
            showDefaultDspExpectedBar,
            showDefaultDspExpectedLine,
            theme.palette.grey,
            theme.palette.primary.main,
        ],
    );

    const getWidgetTooltip = useCallback(
        (widget: SummaryWidgetConfig) => {
            const field = getWidgetReportField(widget);
            if (widget.kind === 'line') {
                return widget.metric === 'plays'
                    ? playsTooltipLabel(FieldReportType.OverTime, null)
                    : impactsTooltipLabel(
                          FieldReportType.OverTime,
                          null,
                          showDefaultDspExpectedLine(widget)
                              ? expectedLabelImpacts
                              : expectedLabelForImpactsCharts,
                      );
            }

            if (widget.kind === 'bar') {
                return widget.metric === 'plays'
                    ? playsTooltipLabel(FieldReportType.Overall, field)
                    : impactsTooltipLabel(FieldReportType.Overall, field);
            }

            return undefined;
        },
        [
            expectedLabelForImpactsCharts,
            expectedLabelImpacts,
            getWidgetReportField,
            impactsTooltipLabel,
            playsTooltipLabel,
            showDefaultDspExpectedLine,
        ],
    );

    const getWidgetXAxisLabels = useCallback(
        (widget: SummaryWidgetConfig) => {
            if (
                widget.kind === 'line' &&
                widget.groupBy.length === 1 &&
                widget.groupBy[0] === 'date'
            ) {
                return [dateLabelLabel];
            }

            const groupBy = widget.groupBy.length > 0 ? widget.groupBy : ['date'];

            return groupBy.map(value =>
                t(
                    SUMMARY_WIDGET_GROUP_BY_TRANSLATION_KEYS[value] ??
                        (`Modules.Main.Campaigns.Overview.Reports.fields.${value}` as TranslationPath),
                ),
            );
        },
        [dateLabelLabel, t],
    );

    const getWidgetFilterOptionLabels = useCallback(
        (widget: SummaryWidgetConfig) => {
            if (widget.metric !== 'impacts') return undefined;

            if (showDefaultDspExpectedLine(widget)) {
                return {
                    expected: expectedLabelImpacts,
                    delivered: actualDeliveredLabelImpacts,
                    both: 'Both',
                };
            }

            if (showDefaultDspExpectedBar(widget)) {
                return {
                    expected: expectedLabelImpacts,
                    delivered: actualDeliveredLabelImpacts,
                    both: 'Both',
                };
            }

            if (
                isDsp &&
                (isGeopathOnly || isAverageAudienceMode) &&
                selectedAudienceLabelForCharts
            ) {
                return {
                    expected: 'Total',
                    delivered: selectedAudienceLabelForCharts,
                    both: 'Both',
                };
            }

            return undefined;
        },
        [
            actualDeliveredLabelImpacts,
            expectedLabelImpacts,
            isAverageAudienceMode,
            isDsp,
            isGeopathOnly,
            selectedAudienceLabelForCharts,
            showDefaultDspExpectedBar,
            showDefaultDspExpectedLine,
        ],
    );

    const buildWidgetReport = useCallback(
        (widget: SummaryWidgetConfig): SummaryWidgetDefinition => {
            const isDateBarWidget =
                widget.kind === 'bar' &&
                widget.groupBy.length === 1 &&
                widget.groupBy[0] === 'date';
            if (widget.kind === 'bar') {
                const currentWindow = latestSummaryWindow.current;
                const useFetching = widget.source === 'groupByField' && !isDateBarWidget;
                const widgetParts = getWidgetParts(widget);
                const xAxisLabels = getWidgetXAxisLabels(widget);
                const tooltipRenderer = getWidgetTooltip(widget);
                const standardExportDataTransform = (rows: any[]) =>
                    buildStandardSummaryExportData(rows, xAxisLabels, dateLabelLabel);
                const chartExportDataTransform = isDsp
                    ? (rows: any[]) => buildSummaryExportData(rows, widget.metric, xAxisLabels)
                    : standardExportDataTransform;
                const fallbackWidgetStartDate = currentWindow?.start ?? initialStartDate;
                const fallbackWidgetEndDate = currentWindow?.end ?? initialEndDate;
                const widgetStartDate = currentWindow?.start;
                const widgetEndDate = currentWindow?.end;
                const widgetTimeframe = currentWindow?.timeframe;
                const fetchWidgetRows = async (
                    start: Date,
                    end: Date,
                    timeFrame: ReportDateTimeframe,
                ) => fetchExtraWidgetData(widget, start, end, timeFrame);
                const widgetRequestKey = JSON.stringify({
                    widgetId: widget.id,
                    metric: widget.metric,
                    groupBy: widget.groupBy,
                    source,
                    environmentMode,
                    fieldFilters,
                    isAverageAudienceMode,
                    isGeopathOnly,
                    selectedAudienceLabelForCharts,
                    targetSeriesKey,
                });
                const canOverlay = widget.groupBy.length === 1;

                // Builds the report config for one view slot (small or expanded).
                // The small view caps the leaderboard at the default top-N; the
                // expanded view shows every row (topN unbounded).
                const buildBarView = (rawMode: string, expanded: boolean): any => {
                    const mode = rawMode === 'overlayBars' && !canOverlay ? 'chart' : rawMode;
                    const base = {
                        type: ReportType.Custom,
                        namePrefix: `${id}_${widget.id}`,
                        widgetId: widget.id,
                        xAxisDataKey: dateLabelLabel,
                        parts: widgetParts,
                        renderTooltipLabel: tooltipRenderer,
                    };

                    if (mode === 'leaderboard') {
                        const expandedTopN = expanded
                            ? { topN: Number.MAX_SAFE_INTEGER }
                            : { topN: widget.smallItemLimit ?? 7 };
                        const leaderboardDefaults = {
                            initialSortDir: widget.defaultSort === 'asc' ? 'asc' : 'desc',
                            initialPercentOn: widget.defaultShowPercent === true,
                        };
                        return useFetching
                            ? {
                                  ...base,
                                  RenderReport: LeaderboardFetchingWidgetReport,
                                  fetchData: fetchWidgetRows,
                                  requestKey: widgetRequestKey,
                                  startDate: widgetStartDate,
                                  endDate: widgetEndDate,
                                  timeframe: widgetTimeframe,
                                  ...leaderboardDefaults,
                                  ...expandedTopN,
                              }
                            : {
                                  ...base,
                                  RenderReport: LeaderboardWidgetReport,
                                  data: getWidgetData(widget) as any,
                                  loadingState: summaryLoadingState,
                                  preserveOrder: widget.groupBy[0] === 'day_of_week',
                                  ...leaderboardDefaults,
                                  ...expandedTopN,
                              };
                    }

                    if (mode === 'overlayBars') {
                        return useFetching
                            ? {
                                  ...base,
                                  RenderReport: OverlayBarsFetchingWidgetReport,
                                  fetchData: fetchWidgetRows,
                                  requestKey: widgetRequestKey,
                                  startDate: widgetStartDate,
                                  endDate: widgetEndDate,
                                  timeframe: widgetTimeframe,
                              }
                            : {
                                  ...base,
                                  RenderReport: OverlayBarsWidgetReport,
                                  data: getWidgetData(widget) as any,
                                  loadingState: summaryLoadingState,
                              };
                    }

                    const orientation = expanded
                        ? widget.expandedBarOrientation
                        : widget.barOrientation;
                    const isTemporalGroupBy = ['date', 'day_of_week', 'hour'].includes(
                        widget.groupBy[0],
                    );
                    const smallChartTopBars =
                        !expanded && !isTemporalGroupBy ? widget.smallItemLimit ?? 7 : undefined;
                    return useFetching
                        ? {
                              ...base,
                              RenderReport: GroupByFieldWidgetReport,
                              reportType: ReportType.Bar,
                              startDate: widgetStartDate,
                              endDate: widgetEndDate,
                              timeframe: widgetTimeframe,
                              requestKey: widgetRequestKey,
                              topBars: smallChartTopBars,
                              defaultShowNumbers: widget.defaultShowNumbers ?? isDsp,
                              defaultShowPercent: widget.defaultShowPercent ?? false,
                              defaultViewFilter: widget.defaultViewFilter ?? 'Both',
                              initialSort:
                                  widget.defaultSort === 'none' ? null : widget.defaultSort,
                              allowPercentToggle:
                                  showDefaultDspTargetPercent(widget) ||
                                  widget.defaultShowPercent != null,
                              percentOfTotal: showDefaultDspTargetPercent(widget),
                              percentOfTotalLabel: shareOfTotalLabel,
                              percentLabelDecimals,
                              filterOptionLabels: getWidgetFilterOptionLabels(widget),
                              orientation,
                              exportDataTransform: chartExportDataTransform,
                              fetchData: fetchWidgetRows,
                          }
                        : {
                              ...base,
                              RenderReport: StaticChartWidgetReport,
                              reportType: ReportType.Bar,
                              startDate: fallbackWidgetStartDate,
                              endDate: fallbackWidgetEndDate,
                              data: getWidgetData(widget) as any[],
                              loadingState: summaryLoadingState,
                              topBars: smallChartTopBars,
                              defaultShowNumbers: widget.defaultShowNumbers ?? isDsp,
                              defaultShowPercent: widget.defaultShowPercent ?? false,
                              defaultViewFilter: widget.defaultViewFilter ?? 'Both',
                              initialSort:
                                  widget.defaultSort === 'none' ? null : widget.defaultSort,
                              allowPercentToggle:
                                  showDefaultDspTargetPercent(widget) ||
                                  widget.defaultShowPercent != null,
                              percentOfTotal: showDefaultDspTargetPercent(widget),
                              percentOfTotalLabel: shareOfTotalLabel,
                              percentLabelDecimals,
                              filterOptionLabels: getWidgetFilterOptionLabels(widget),
                              orientation,
                              exportDataTransform: chartExportDataTransform,
                          };
                };

                const minView = widget.displayMode ?? 'chart';
                const expandedView = widget.expandedDisplayMode ?? 'chart';
                return {
                    id: widget.id,
                    report: buildBarView(minView, false),
                    expandedReport: buildBarView(expandedView, true),
                };
            }

            if (widget.source === 'groupByField' && !isDateBarWidget) {
                const currentWindow = latestSummaryWindow.current;
                const reportType = widget.kind === 'line' ? ReportType.Line : ReportType.Bar;
                const chartParts = getWidgetParts(widget);
                const tooltipRenderer = getWidgetTooltip(widget);
                const xAxisLabels = getWidgetXAxisLabels(widget);
                const standardExportDataTransform = (rows: any[]) =>
                    buildStandardSummaryExportData(rows, xAxisLabels, dateLabelLabel);
                const widgetStartDate = currentWindow?.start;
                const widgetEndDate = currentWindow?.end;
                const widgetTimeframe = currentWindow?.timeframe;
                const fetchWidgetRows = async (
                    start: Date,
                    end: Date,
                    timeFrame: ReportDateTimeframe,
                ) => fetchExtraWidgetData(widget, start, end, timeFrame);
                const widgetRequestKey = JSON.stringify({
                    widgetId: widget.id,
                    metric: widget.metric,
                    groupBy: widget.groupBy,
                    source,
                    environmentMode,
                    fieldFilters,
                    isAverageAudienceMode,
                    isGeopathOnly,
                    selectedAudienceLabelForCharts,
                    targetSeriesKey,
                });

                const groupByReport = {
                    type: ReportType.Custom,
                    namePrefix: `${id}_${widget.id}`,
                    RenderReport: GroupByFieldWidgetReport,
                    widgetId: widget.id,
                    reportType,
                    startDate: widgetStartDate,
                    endDate: widgetEndDate,
                    timeframe: widgetTimeframe,
                    xAxisDataKey: dateLabelLabel,
                    parts: chartParts,
                    requestKey: widgetRequestKey,
                    renderTooltipLabel: tooltipRenderer,
                    defaultShowNumbers: widget.defaultShowNumbers ?? isDsp,
                    defaultShowPercent: widget.defaultShowPercent ?? false,
                    defaultViewFilter: widget.defaultViewFilter ?? 'Both',
                    initialSort: widget.defaultSort === 'none' ? null : widget.defaultSort,
                    allowPercentToggle:
                        showDefaultDspTargetPercent(widget) || widget.defaultShowPercent != null,
                    percentOfTotal: showDefaultDspTargetPercent(widget),
                    percentOfTotalLabel: shareOfTotalLabel,
                    percentLabelDecimals,
                    filterOptionLabels: getWidgetFilterOptionLabels(widget),
                    orientation: widget.barOrientation,
                    allowAccumulateToggle: widget.kind === 'line',
                    defaultCumulative: widget.cumulative !== false,
                    exportDataTransform: isDsp
                        ? rows => buildSummaryExportData(rows, widget.metric, xAxisLabels)
                        : standardExportDataTransform,
                    fetchData: fetchWidgetRows,
                };
                return {
                    id: widget.id,
                    report: groupByReport,
                };
            }

            const fallbackWidgetStartDate = latestSummaryWindow.current?.start ?? initialStartDate;
            const fallbackWidgetEndDate = latestSummaryWindow.current?.end ?? initialEndDate;
            const xAxisLabels = getWidgetXAxisLabels(widget);
            const widgetParts = getWidgetParts(widget);
            const standardExportDataTransform = (rows: any[]) =>
                buildStandardSummaryExportData(rows, xAxisLabels, dateLabelLabel);

            if (widget.kind === 'map') {
                return {
                    id: widget.id,
                    report: {
                        type: ReportType.Custom,
                        namePrefix: `${id}_${widget.id}`,
                        RenderReport: MapWidgetReport,
                        widgetId: widget.id,
                        campaignId: id,
                        data: getWidgetData(widget) as any,
                        mapDataType: widget.mapDataType as any,
                        mapDisplayMode: widget.mapDisplayMode as any,
                        loadingState: summaryLoadingState,
                    },
                };
            }

            const staticReport = {
                type: ReportType.Custom,
                namePrefix: `${id}_${widget.id}`,
                RenderReport: StaticChartWidgetReport,
                widgetId: widget.id,
                reportType: widget.kind === 'line' ? ReportType.Line : ReportType.Bar,
                startDate: fallbackWidgetStartDate,
                endDate: fallbackWidgetEndDate,
                xAxisDataKey: dateLabelLabel,
                data: getWidgetData(widget) as any[],
                parts: widgetParts,
                loadingState: summaryLoadingState,
                renderTooltipLabel: getWidgetTooltip(widget),
                defaultShowNumbers: widget.defaultShowNumbers ?? isDsp,
                defaultShowPercent: widget.defaultShowPercent ?? false,
                defaultViewFilter: widget.defaultViewFilter ?? 'Both',
                initialSort: widget.defaultSort === 'none' ? null : widget.defaultSort,
                allowPercentToggle:
                    showDefaultDspTargetPercent(widget) || widget.defaultShowPercent != null,
                percentOfTotal: showDefaultDspTargetPercent(widget),
                percentOfTotalLabel: shareOfTotalLabel,
                percentLabelDecimals,
                filterOptionLabels: getWidgetFilterOptionLabels(widget),
                orientation: widget.barOrientation,
                allowAccumulateToggle: widget.kind === 'line',
                defaultCumulative: widget.cumulative !== false,
                exportDataTransform: isDsp
                    ? rows => buildSummaryExportData(rows, widget.metric, xAxisLabels)
                    : standardExportDataTransform,
            };
            return {
                id: widget.id,
                report: staticReport,
            };
        },
        [
            buildSummaryExportData,
            dateLabelLabel,
            environmentMode,
            fetchExtraWidgetData,
            fieldFilters,
            getWidgetData,
            getWidgetFilterOptionLabels,
            getWidgetParts,
            getWidgetTooltip,
            getWidgetXAxisLabels,
            id,
            initialEndDate,
            initialStartDate,
            isAverageAudienceMode,
            isDsp,
            isGeopathOnly,
            latestSummaryWindow,
            percentLabelDecimals,
            selectedAudienceLabelForCharts,
            shareOfTotalLabel,
            showDefaultDspTargetPercent,
            source,
            summaryLoadingState,
            targetSeriesKey,
        ],
    );

    return useMemo<VisibleSummaryWidgetDefinition[]>(
        () =>
            visibleWidgetConfigs.map(widget => ({
                ...buildWidgetReport(widget),
                title: getSummaryWidgetTitle(widget),
                expandedTitle:
                    widget.displayMode === 'leaderboard' || widget.displayMode === 'overlayBars'
                        ? getSummaryWidgetTitle({ ...widget, displayMode: 'chart' })
                        : undefined,
                isHeatmap: widget.kind === 'map',
                isPrimary: widget.isPrimary === true,
                kind: widget.kind,
                displayMode: widget.displayMode,
            })),
        [buildWidgetReport, getSummaryWidgetTitle, visibleWidgetConfigs],
    );
};
